/**
 * pageSelection — which PAGE node the canvas has selected, for the bar.
 *
 * Widgets and sections live in the grid store's selection; a page is an
 * xyflow node, selected by clicking its frame, and nothing outside the canvas
 * could see that. The selection bar's page state (section spacing, for now)
 * needs it, so PageNode publishes its `selected` flag here — a module
 * singleton like altHeld, because the bar and the canvas live in different
 * subtrees. The grid selection always wins: a selected widget's verbs show
 * even while its page node is also selected.
 */
import { useSyncExternalStore } from "react";

export interface SelectedPage {
  /** The page node id — also the id of its page grid. */
  readonly pageId: string;
  readonly label: string;
}

let current: SelectedPage | null = null;
const listeners = new Set<() => void>();

function emit(): void {
  for (const l of listeners) l();
}

/** PageNode: call with `selected` on every change of its selected prop. */
export function setPageSelected(page: SelectedPage, selected: boolean): void {
  if (selected) {
    if (current?.pageId === page.pageId && current.label === page.label) return;
    current = page;
    emit();
    return;
  }
  // Only the page that holds the slot may clear it — a stale unmount of
  // another node must not drop a fresh selection.
  if (current?.pageId !== page.pageId) return;
  current = null;
  emit();
}

export function selectedPage(): SelectedPage | null {
  return current;
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export function useSelectedPage(): SelectedPage | null {
  return useSyncExternalStore(subscribe, selectedPage, () => null);
}

/** Test seam. */
export function resetPageSelection(): void {
  current = null;
  emit();
}
